'use client';

import { HintIcon } from '@/components';
import { IconBox } from '@/components/common/Hint/styled';
import { ClickAwayListener, Tooltip, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { runtimeConfig } from '@/lib';
import { PATHS } from '@/constants';
import { directLinkTooltipSlotProps } from './DirectLinkHint.styled';

type Props = {
  value: string;
  descriptionLabel: string;
  allowedCharsLabel: string;
  yourEntryLabel: string;
  yourLinkLabel: string;
};

export const DirectLinkHint: FC<Props> = ({
  value,
  descriptionLabel,
  allowedCharsLabel,
  yourEntryLabel,
  yourLinkLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  const handleClose = () => setOpen(false);
  const handleToggle = () => setOpen((prev) => !prev);

  const title = (
    <>
      <Typography fontSize={12} lineHeight={1.5} mb="6px">
        {descriptionLabel}
      </Typography>
      <Typography fontSize={12} lineHeight={1.5} mb="6px">
        {allowedCharsLabel}
      </Typography>
      {value && (
        <>
          <Typography fontSize={12} lineHeight={1.5}>
            {yourEntryLabel} <strong>{value}</strong>
          </Typography>
          <Typography fontSize={12} lineHeight={1.5}>
            {yourLinkLabel}{' '}
            <strong>{`${runtimeConfig.BASE_URL ?? ''}/${PATHS.BROKERS}/${value}`}</strong>
          </Typography>
        </>
      )}
    </>
  );

  const icon = (
    <IconBox>
      <HintIcon />
    </IconBox>
  );

  if (isTouchDevice) {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <Tooltip
          open={open}
          onClose={handleClose}
          title={title}
          placement="bottom-start"
          disableFocusListener
          disableHoverListener
          disableTouchListener
          TransitionProps={{ timeout: 0 }}
          slotProps={directLinkTooltipSlotProps}
        >
          <div
            role="button"
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
            }}
            style={{ display: 'inline-flex', cursor: 'pointer' }}
          >
            {icon}
          </div>
        </Tooltip>
      </ClickAwayListener>
    );
  }

  return (
    <Tooltip
      TransitionProps={{ timeout: 0 }}
      title={title}
      placement="top"
      slotProps={directLinkTooltipSlotProps}
    >
      <div style={{ display: 'inline-flex' }}>{icon}</div>
    </Tooltip>
  );
};
